import { Athlete } from '../types/mongodb';

// Default color palette for charts (fallback when athlete doesn't have a color assigned)
const DEFAULT_CHART_COLORS = [
  '#ff9600', '#0066cc', '#00cc66', '#cc0066', '#6600cc',
  '#cc6600', '#00cccc', '#ff0066', '#0066ff', '#ff6600',
  '#33cc33', '#cc33cc', '#3366cc', '#ff3366', '#66cc00',
  '#cc0033', '#006699', '#ff9900', '#9900cc', '#00ff99',
];

/**
 * Get color for an athlete. Returns the athlete's assigned color if available,
 * otherwise returns a color from the default palette based on athlete ID.
 * @param athlete - The athlete object (can be just { _id, color } or full athlete)
 * @param index - Optional index for fallback color selection
 * @returns Hex color string
 */
export const getAthleteColor = (athlete: { _id: string; color?: string | null } | null, index: number = 0): string => {
  if (athlete?.color) {
    return athlete.color;
  }
  
  // Use athlete ID to consistently assign a color from the default palette
  // This ensures the same athlete always gets the same color even without an assigned color
  if (athlete?._id) {
    // Convert ObjectId to a number for consistent color selection
    const idHash = athlete._id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return DEFAULT_CHART_COLORS[idHash % DEFAULT_CHART_COLORS.length];
  }
  
  // Fallback to index-based color
  return DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length];
};

/**
 * Get colors for multiple athletes. Returns an array of colors in the same order as athletes.
 * @param athletes - Array of athlete objects
 * @returns Array of hex color strings
 */
export const getAthleteColors = (athletes: Array<{ _id: string; color?: string | null }>): string[] => {
  return athletes.map((athlete, index) => getAthleteColor(athlete, index));
};

/**
 * Get color for an athlete by ID from a map of athletes.
 * Useful when you have a map/object of athletes keyed by ID.
 * @param athleteId - The athlete ID
 * @param athletesMap - Map or object of athletes keyed by ID
 * @param index - Optional index for fallback color selection
 * @returns Hex color string
 */
export const getAthleteColorById = (
  athleteId: string,
  athletesMap: Record<string, { _id: string; color?: string | null }> | Map<string, { _id: string; color?: string | null }>,
  index: number = 0
): string => {
  const athlete = athletesMap instanceof Map 
    ? athletesMap.get(athleteId)
    : athletesMap[athleteId];
  
  return getAthleteColor(athlete || { _id: athleteId }, index);
};



